import { Box, useTheme, IconButton } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { tokens } from "../../../../../theme";
import ShCopyButton from '../../../../theme/ShCopy';

import ShLabel from "../../../../theme/label/ShLabel";
import Text from "../../../../theme/text/Text";

import { useTranslation } from "react-i18next";
import { useGetOrdersQuery } from "../../../../../newapi/order/ordersSlice";
import ShTextSkeleton from '../../../../theme/skeleton/ShTextSkeleton';
import ShLabelSkeleton from "../../../../theme/skeleton/ShLabelSkeleton";
import ShDataGrid2 from '../../../../theme/datagrid2/ShDataGrid2';
import SidepanelLoader from '../../../../theme/sidepanel2/SidepanelLoader';
import { getOrderStateData } from "../../../../../utils/labelColorText";
import { general_states } from "../../../../../utils/staticEnums";
import OrdersCard from "../../../../global/cards/orders/OrdersCard";

const SpOrderDetails = SidepanelLoader(() => import("../../../../../components/global/Sidepanels/SpOrderDetails/SpOrderDetails"));

const GridActions = (props) => {
    return <></>;
}

const gridOptions = {
    export: false,
    disableCustomActionsOnPhone: true,
    isSidepanel: true,
};

const Orders = (props) => {
    const {t, i18n} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [spLoading, setSpLoading] = useState();
    const [isExtended, setIsExtended] = useState(false);

    const [config, setConfig] = useState({
        // Filter orders for this specific Etailize webshop
        etailizeWebshopId: props.etailizeWebshopId,
        webshopCredentialsId: props.data?.id, // Use the webshop credentials ID from the parent data
        state: [
            general_states.OPEN, 
            general_states.PICKING_IN_PROGRESS, 
            general_states.FULLYPICKED, 
            general_states.SHIPPED, 
            general_states.CANCELLED,
            general_states.REPLENISH,
            general_states.ECHECK,
            general_states.ECHECKPAUSED,
            general_states.BACKORDERED
        ]
    });

    const [orderId, setOrderId] = useState();
    const orderIdRef = useRef(orderId);
    
    const getOrderDetails = (params) => {
        if (!params) return;
    
        const neworderId = parseInt(params.row.id);
        // Only update state if the orderId has actually changed
        if (orderIdRef.current !== neworderId) {
            if(!spLoading) {
                setSpLoading(true);
            }
            setOrderId(neworderId);
            orderIdRef.current = neworderId; // Update the ref to the new value
        }
        setIsExtended(true);
    };

    // Grid actions are handled by the ShDataGrid2 component directly

    // Update config when parent data changes
    useEffect(() => {
        if (props.data?.id) {
            setConfig(prev => ({
                ...prev,
                webshopCredentialsId: props.data.id,
                etailizeWebshopId: props.etailizeWebshopId
            }));
        }
    }, [props.data?.id, props.etailizeWebshopId]);
    
    const columns = [
        {
            field: "barcode",
            headerName: t('order'),
            flex: 1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return (
                    <Box sx={{
                        minWidth:"100%",
                        height:"100%",
                        display:"flex",
                        alignItems:"center",
                        position:"relative",
                        whiteSpace:"nowrap",
                        wordBreak:"keep-all",
                        textOverflow:"ellipsis",
                        overflow:"hidden",
                        ".copyel": {
                            opacity:0,
                        },
                        "&:hover .copyel": {
                            opacity:1,
                        }
                    }}>
                        <Text noBreak>{params.row.barcode}</Text>
                        <ShCopyButton 
                            styles={{button:{
                                background:theme.palette.mode === "dark" ? colors.grey[600] :  colors.grey[200],
                                color: colors.txt["primary"],
                                "&:hover" : {
                                    background:theme.palette.mode === "dark" ? colors.grey[500] :colors.grey[300],
                                }
                            }}} 
                            sx={{position:"absolute",right:0,zIndex:3000}} 
                            className={"copyel"} 
                            closeAfter={1500} 
                            value={params.row.barcode}
                        ></ShCopyButton>
                    </Box>
                );
            }
        },
        {field: "deliveryName", headerName: t("name"), flex: 1, 
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return params.row.deliveryName
            }
        },
        {field: "webshopreference", headerName: t("webshopreference"), flex: 1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                return (<Text>{`${params.row.webshopreference} (${params.row.deliveryCountry ? params.row.deliveryCountry.toUpperCase() : ""})`}</Text>)
            }
        },
        {
            field: "marketplace", 
            headerName: t("marketplace"),
            flex: 1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                // Show the marketplace name for Etailize orders
                return (
                    <Text>{params.row.marketplace || props.data?.marketplace || 'e-tailize'}</Text>
                );
            }
        },
        {
            field: "state", 
            headerName: t("status"),
            flex: 1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShLabelSkeleton/>
                }
                const state = params.row.state === general_states.REPLENISH ? general_states.OPEN : params.row.state;
                
                const { labelcolor, labeltext } = getOrderStateData(state, theme);
              
                return (
                    <ShLabel size={32} palette="normal" variant={labelcolor}>
                      {t(labeltext)}
                    </ShLabel>
                  );
              }
        },
        {
            field: "createdAt", 
            headerName: t("date_created"),
            flex: 1,
            renderCell: (params) => {
                if(params.row.skeleton) {
                    return <ShTextSkeleton/>
                }
                
                return new Date(params.row.createdAt).toLocaleString(i18n.language, {
                    dateStyle: 'short',
                    timeStyle: 'short'
                });
              }
        },
        {
            field: "phoneView", 
            headerName: "PhoneView",
            cellClassName: "phone-column--cell",
            renderCell: (params) => {
                return (
                    <OrdersCard skeleton={params.row.skeleton ?? false} {...params.row}  />
                )
            }
        }
    ];

    return (
        <Box height={1} display={"flex"} flexDirection={"column"} overflow={"auto"}>
            <ShDataGrid2 key={theme.palette.mode}
                itemId={props.etailizeWebshopId}
                config={config}
                content={useGetOrdersQuery}
                gridActions={<GridActions/>}
                title={t("orders")}
                gridOptions={gridOptions}
                isSidepanelGrid
                columns={columns}
                onRowClick={getOrderDetails}
            />

            {spLoading && 
                <SpOrderDetails
                    isExtended={isExtended}
                    setIsExtended={setIsExtended}
                    orderId={orderId}
                    canReserveOrderLines={true}
                />
            }
        </Box>
    );
}

export default Orders;